import React from 'react';
import { Box, Dialog, DialogContent, DialogActions, IconButton, Button, CircularProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@mui/material/styles';

/**
 * Reusable Confirmation Dialog Component
 *
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Handler for closing dialog
 * @param {string|object} title - Dialog title (can be string or FormattedMessage id)
 * @param {string|object} message - Main dialog message (can be string or FormattedMessage id)
 * @param {string|object} note - Optional secondary message/note (can be string or FormattedMessage id)
 * @param {string|object} cancelText - Cancel button text (default: "noCommon")
 * @param {string|object} confirmText - Confirm button text (default: "yesCommon")
 * @param {function} onConfirm - Handler for confirm action (async supported)
 * @param {boolean} loading - Shows loading state on confirm button
 * @param {string} maxWidth - Dialog max width (xs, sm, md, lg, xl)
 * @param {boolean} fullWidth - Dialog full width
 * @param {object} titleProps - Additional props for the title Typography (sx merges with defaults)
 * @param {object} messageProps - Additional props for the message Typography (sx merges with defaults)
 * @param {object} contentProps - Additional props for DialogContent
 * @param {object} actionsProps - Additional props for DialogActions
 * @param {boolean} disableBackdropClick - Disable closing on backdrop click
 * @param {boolean} hideCloseButton - Hide the top-right close (X) icon (default: false)
 * @param {string} confirmColor - Confirm button color (primary, secondary, error, etc.)
 * @param {string} confirmVariant - Confirm button variant (text, outlined, contained)
 * @param {object} dialogProps - Additional props for Dialog
 * @param {object} buttonProps - Additional props for confirm button (sx, etc.)
 * @param {React.ReactNode} content - Optional custom content to render inside DialogContent (e.g., forms)
 * @param {React.ReactNode} children - Alias for custom content (rendered after message/note)
 * @param {React.ReactNode} topContent - Optional banner/snackbar rendered flush at the top of the dialog paper
 * @param {string} closeButtonLabel - Accessible name for the top-right close (X) icon. Optional and
 *   undefined by default: the icon carries no text, so without this it reaches screen readers as an
 *   unnamed button. Deliberately NOT defaulted to a hardcoded string — that would put an untranslated
 *   English label on all ~53 existing call sites at once. Pass an already-localized string (a dialog
 *   replacing a hand-rolled one that had its own aria-label should carry it over).
 */
const ConfirmDialog = ({
  open = false,
  onClose,
  image,
  imgBgColor,
  title,
  message,
  note,
  cancelText = 'noCommon',
  confirmText = 'yesCommon',
  onConfirm,
  loading = false,
  maxWidth = 'sm',
  fullWidth = true,
  titleProps = {},
  messageProps = {},
  contentProps = {},
  actionsProps = {},
  disableBackdropClick = false,
  hideCancel = false,
  hideCloseButton = false,
  confirmColor = 'primary',
  confirmVariant = 'text',
  dialogProps = {},
  buttonProps = {},
  content,
  children,
  topContent,
  id = 'confirmDialog',
  confirmButtonId = 'confirmBtn',
  cancelButtonId = 'cancelBtn',
  closeButtonLabel
}) => {
  const theme = useTheme();
  const { sx: messageSx, ...messageTypographyProps } = messageProps;
  const { PaperProps: dialogPaperProps = {}, ...restDialogProps } = dialogProps;
  const { sx: dialogPaperSx, ...dialogPaperRest } = dialogPaperProps;
  const { sx: contentSx, ...restContentProps } = contentProps;

  const getImageBgColor = () => {
    if (!imgBgColor) {
      return 'blue.50';
    }

    // Handle semantic colors like 'error', 'primary', etc.
    if (theme.palette[imgBgColor]) {
      return 'dialogError.light';
    }

    // fallback if direct color passed like '#f00'
    return imgBgColor;
  };

  const getTitleColor = () => {
    // If no special color → default
    if (!imgBgColor) return 'grey.900';

    // If semantic color like 'error'
    if (imgBgColor === 'error') {
      return '#C62828'; // same for both modes as per your requirement
    }

    // Optional: handle other semantic colors (future-proof)
    if (theme.palette[imgBgColor]) {
      return theme.palette[imgBgColor].main;
    }

    // fallback
    return 'grey.900';
  };

  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (onClose) onClose();
  };

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  const renderText = (text) => {
    if (!text) return null;
    if (typeof text === 'string') {
      return <FormattedMessage id={text} defaultMessage={text} />;
    }
    if (typeof text === 'object' && text.id) {
      return <FormattedMessage id={text.id} defaultMessage={text.defaultMessage || text.id} values={text.values} />;
    }
    return text;
  };

  return (
    <Dialog
      id={id}
      open={open}
      // While `loading`, the action is in flight: swallow backdrop-click and Escape. Dismissing here
      // would hide a running mutation behind a closed dialog and let the user start another.
      onClose={loading ? undefined : handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      {...restDialogProps}
      PaperProps={{
        ...dialogPaperRest,
        sx: {
          borderRadius: 4,
          textAlign: 'center',
          p: { xs: 2, sm: 5 },
          position: 'relative',
          ...dialogPaperSx
        }
      }}
    >
      {!hideCloseButton && (
        <IconButton
          onClick={onClose}
          disabled={loading}
          aria-label={closeButtonLabel}
          size="small"
          sx={{
            position: 'absolute',
            right: 30,
            top: 30,
            zIndex: 1
          }}
          id="close-welcome-modal-button"
        >
          <CloseIcon />
        </IconButton>
      )}
      {(topContent || message || note || content || children || image || title) && (
        <DialogContent
          {...restContentProps}
          sx={{
            pt: 0,
            pb: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            ...contentSx
          }}
        >
          {topContent && <Box sx={{ width: '100%', maxWidth: '100%', alignSelf: 'stretch', mt: 4, mb: 0 }}>{topContent}</Box>}
          {image && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 2,
                mb: 3,
                width: '87px',
                height: '87px',
                minHeight: '87px',
                borderRadius: '100%',
                p: 2,
                backgroundColor: getImageBgColor()
              }}
            >
              {typeof image === 'string' ? <img src={image} alt="dialog" style={{ width: '47px' }} /> : image}
            </Box>
          )}

          {/* Title */}

          {title && (
            <Typography
              variant="h1"
              sx={{
                fontWeight: 500,
                mb: 1.25,
                color: getTitleColor()
              }}
              {...titleProps}
            >
              {renderText(title)}
            </Typography>
          )}

          {/* Message */}
          {message && (
            <Typography
              variant="body1"
              sx={{
                color: 'grey.700',
                ...messageSx
              }}
              {...messageTypographyProps}
            >
              {renderText(message)}
            </Typography>
          )}

          {/* Note */}
          {note && (
            <Typography
              variant="body1"
              sx={{
                color: 'grey.500',
                mt: message ? 2 : 0,
                mb: 5
              }}
            >
              {renderText(note)}
            </Typography>
          )}

          {(content || children) && (
            <Typography component="div" sx={{ mt: message || note ? 2 : 0 }}>
              {content || children}
            </Typography>
          )}
        </DialogContent>
      )}

      <DialogActions {...actionsProps} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {!hideCancel && (
          <Button variant="outlined" id={cancelButtonId} onClick={onClose} disabled={loading} sx={{ minWidth: '86px' }}>
            {renderText(cancelText)}
          </Button>
        )}
        <Button
          id={confirmButtonId}
          onClick={handleConfirm}
          autoFocus
          color={buttonProps.color || confirmColor}
          variant={buttonProps.variant || confirmVariant}
          disabled={loading || Boolean(buttonProps.disabled)}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ minWidth: '86px', ...buttonProps.sx }}
        >
          {renderText(confirmText)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
